// eslint-disable-next-line no-unused-vars
import React, { memo, useMemo } from 'react';
import { uniqueId, camelCase, omit, startCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { stringify } from 'query-string';
import { House } from 'lucide-react';

import useRouter from 'Hooks/useRouter';
import useBreadcrumbs from 'Hooks/useBreadcrumbs';

import BreadcrumbsComponent from 'Components/Breadcrumbs/Breadcrumbs';

const BreadCrumbs = memo(() => {
  const { t } = useTranslation();
  const { location, params, query } = useRouter();
  const {
    lists,
    order,
    categories,
    activeCategoryParts,
    findActiveCategoryParts,
    product,
  } = useBreadcrumbs();

  const parts = useMemo(() => {
    if (
      !location.pathname.trim()
      || location.pathname.includes('/checkout')
    ) return null;

    // strip promotion param
    const cleanQuery = omit(query, ['promotion']);
    const cleanSearch = stringify(cleanQuery) ? `?${stringify(cleanQuery)}` : '';

    // Product details page
    if (location.pathname.match(/^\/products\/[\w+?!'"_\\-]+\/pd\/[\w\d]{32}\/?/g)) {
      if (product === null) return null;

      if (
        !product?.categoryId
        || categories.length <= 1
      ) {
        return Array.from({ length: 4 }, () => ({
          id: uniqueId(),
          skeleton: true
        }));
      }

      const categoryParts = findActiveCategoryParts(categories, product?.categoryId);
      if (categoryParts.length <= 1) return null;

      // let productTitle = product?.title.substr(0, 150);
      // if (product?.title?.length > productTitle.length) {
      //   productTitle += '...';
      // }

      return [{
        id: 'products',
        name: t('products.products'),
        path: '/products',
        search: cleanSearch
      }].concat(
        categoryParts.map(cat => ({
          id: cat.id,
          name: cat.name,
          path: `/products/${ cat.slug }-c${ cat.id }/c`,
          search: cleanSearch
        }))
      );
      // .concat({
      //   id: product?.id,
      //   name: productTitle,
      //   path: location.pathname
      // });
    }

    // Products listing page
    if (
      // eslint-disable-next-line
      location.pathname.match(/^\/products(\/[\w+?!'"_\\-]+)?(\/attributes(\/[\w+?,!'"_\\-]+)+)?(\/brands\/[\w\d\s\-]+)?(\/price\/[\d,]+)?(\/search\/[\d\w\s"',\/\\_\-+]+)?(\/e\/)?(\/c\/?)?/g)
    ) {
      const basePath = [{
        id: 'products',
        name: t('products.products'),
        path: '/products',
        search: cleanSearch
      }];

      if (!activeCategoryParts?.length) {
        return basePath;
      }

      return basePath.concat(
        activeCategoryParts.map(cat => ({
          id: cat.id,
          name: cat.name,
          path: `/products/${ cat.slug }-c${ cat.id }/c`,
          search: cleanSearch
        }))
      );
    }

    // Wishlists
    if (location.pathname.match(/^\/lists\/?/)) {
      const paths = [{
        id: uniqueId('lists'),
        name: t('wishlists.lists'),
        path: '/lists',
        reload: false,
      }];
      if (
        params.id
        && params.id in (lists || {})
      ) {
        paths.push({
          id: params.id,
          name: lists[params.id].name,
          path: `/lists/${params.id}`
        });
      }
      return paths;
    }

    // Orders
    if (location.pathname.match(/^\/orders\/?/)) {
      if (order === null) return null;

      const paths = [{
        id: uniqueId('orders'),
        name: t('orders.orders'),
        path: '/orders',
        reload: false
      }];
      if (
        params.id
        && order?.id === params.id
      ) {
        paths.push({
          id: params.id,
          name: `${t('orders.order')} ${t('common.nr')}: ${order.orderNumber}`,
          path: `/orders/${params.id}`
        });
      }
      return paths;
    }

    // Account
    if (location.pathname.match(/^\/account/)) {
      const matches = location.pathname.replace(/\/c$/, '').match(/\/(\w+)/g);
      return [
        // {
        //   id: uniqueId('account'),
        //   name: t('navigation.account'),
        //   path: '/account',
        //   reload: false
        // },
        {
          id: uniqueId('account'),
          name: t(`navigation.${ matches[1].slice(1) }`),
          path: location.pathname,
          reload: false
        },
      ];
    }

    // Reports
    if (location.pathname.match(/^\/reports\/?/)) {
      const reportRoutes = {
        invoices: { path: '/reports/invoices', key: 'users.menu.invoices' },
        'unpaid-invoices': { path: '/reports/unpaid-invoices', key: 'users.menu.unpaidInvoices' },
        balance: { path: '/reports/balance', key: 'users.menu.balanceDocuments' },
        payments: { path: '/reports/payments', key: 'users.menu.payments' },
        'account-statements': { path: '/reports/account-statements', key: 'users.menu.accountStatements' },
      };

      const paths = [{
        id: uniqueId('reports'),
        name: t('users.menu.reports'),
        path: '/reports/invoices',
        reload: false
      }];

      const reportType = location.pathname.replace('/reports/', '').replace('/', '');
      if (reportType && reportRoutes[reportType]) {
        paths.push({
          id: uniqueId(reportType),
          name: t(reportRoutes[reportType].key),
          path: reportRoutes[reportType].path,
          reload: false
        });
      }

      return paths;
    }

    // Information
    if (location.pathname.match(/^\/information\/?/)) {
      const matches = location.pathname.match(/\/([^/]+)/g);
      return [{
        id: uniqueId('information'),
        name: t(`navigation.${ camelCase(matches[1]) }`),
        path: location.pathname,
        reload: false
      }];
    }

    // Pages
    if (location.pathname.match(/^\/pages\/[^/]+\/?$/)) {
      return [
        {
          id: params.id,
          name: startCase(params.id),
          path: location.pathname,
          reload: false
        }
      ];
    }

    // Addresses
    if (location.pathname.match(/^\/addresses\/?/)) {
      return [{
        id: uniqueId('addresses'),
        name: t('navigation.addresses'),
        path: '/addresses',
        reload: false
      }];
    }

    // Users
    if (location.pathname.match(/^\/users\/?/)) {
      return [{
        id: uniqueId('users'),
        name: t('navigation.users'),
        path: '/users',
        reload: false
      }];
    }

    // Offers list
    if (location.pathname.match(/^\/offers\/?(?:private\/?)?$/)) {
      return [{
        id: 'offers',
        name: t('offers.myOffers'),
        path: '/offers',
        reload: false
      }];
    }

    // Offer editor (hide the private draft identifier from breadcrumbs)
    if (location.pathname.match(/^\/offers\/(?:private\/)?[^/]+\/editor\/?$/)) {
      return [{
        id: 'offers',
        name: t('offers.offers'),
        path: '/offers',
        reload: false
      }, {
        id: 'offer-editor',
        name: t('offers.editorBreadcrumb'),
        path: location.pathname,
        reload: false
      }];
    }

    // Service
    if (location.pathname.match(/^\/service\/?/)) {
      const serviceRoutes = {
        rma: { path: '/service/rma', key: 'users.menu.rma' },
        ppwr: { path: '/service/ppwr', key: 'ppwr.heading' },
        warranties: { path: '/service/warranties', key: 'users.menu.warranties' },
      };

      const paths = [{
        id: uniqueId('service'),
        name: t('users.menu.service'),
        path: '/service/rma',
        reload: false
      }];

      const serviceType = location.pathname.replace('/service/', '').replace('/', '');
      if (serviceType && serviceRoutes[serviceType]) {
        paths.push({
          id: uniqueId(serviceType),
          name: t(serviceRoutes[serviceType].key),
          path: serviceRoutes[serviceType].path,
          reload: false
        });
      }

      return paths;
    }

    const matches = location.pathname.replace(/\/c$/, '').match(/\/(\w+)/g);

    if (
      matches
      && matches?.length > 1
    ) {
      return matches.reduce((acc, match, idx) => {
        const prev = acc[idx - 1];
        const name = match.replace(/^\//, '');
        acc.push({
          id: uniqueId(name),
          name: name.replace(/\w{1}/, name.charAt(0).toUpperCase()),
          path: !prev ? `${match}` : `${prev.path}${match}`,
          search: location.search
        });
        return acc;
      }, []);
    }

    return null;
  }, [
    location.pathname,
    categories,
    lists,
    order,
    params.id,
    location.search,
    activeCategoryParts,
    product,
    t,
  ]);

  if (
    parts?.length
    && parts[0].id !== 'home'
  ) {
    parts.unshift({
      reload: false,
      id: 'home',
      name: <House size={ 24 } />,
      path: '/'
    });
  }

  return <BreadcrumbsComponent parts={ parts || [] } />;
});

BreadCrumbs.displayName = 'BreadcrumbsContainer';

export default BreadCrumbs;
